import React, { useState, useEffect, useCallback, useRef } from 'react';
import NoteEditor from './NoteEditor';
import TagInput from './TagInput';
import VersionHistory from './VersionHistory';
import useAutoSave from '../../hooks/useAutoSave';
import { useNotes } from '../../context/NotesContext';
import { toast } from 'react-toastify';

/**
 * NotesPanel — Right sidebar for creating/editing a single research note.
 *
 * Props:
 *   isOpen           — boolean
 *   onClose          — () => void
 *   noteId           — string|null (null = new note)
 *   initialContent   — { title, html, plainText, companyIds, tags, source, aiContext } | null
 */
export default function NotesPanel({ isOpen, onClose, noteId: externalNoteId, initialContent }) {
    const { fetchNoteById, createNote, updateNote, fetchNotes } = useNotes();

    // ── Local editor state ──────────────────────────────────────────────
    const [noteId, setNoteId] = useState(externalNoteId);
    const [title, setTitle] = useState('');
    const [editorValue, setEditorValue] = useState({ html: '', plainText: '' });
    const [companyIds, setCompanyIds] = useState([]);
    const [tags, setTags] = useState([]);
    const [companyInput, setCompanyInput] = useState('');
    const [version, setVersion] = useState(1);
    const [source, setSource] = useState('manual');
    const [aiContext, setAiContext] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showVersions, setShowVersions] = useState(false);
    const panelRef = useRef(null);

    // ── Auto-save ───────────────────────────────────────────────────────
    const { saveStatus, lastSaved } = useAutoSave(noteId, editorValue, title, {
        delay: 3000,
        enabled: !!noteId,
    });

    // ── Load existing note ──────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;

        if (externalNoteId) {
            setNoteId(externalNoteId);
            fetchNoteById(externalNoteId).then((data) => {
                if (!data) return;
                setTitle(data.title || '');
                // Content may be stored as rich JSON or as HTML string
                const html = typeof data.content === 'string'
                    ? data.content
                    : data.content?.html || data.plainText || '';
                setEditorValue({ html, plainText: data.plainText || '' });
                setCompanyIds(data.companyIds || []);
                setTags(data.tags || []);
                setVersion(data.version || 1);
                setSource(data.source || 'manual');
            });
        } else if (initialContent) {
            // Pre-populated from AI chat "Save to Notes"
            setNoteId(null);
            setTitle(initialContent.title || '');
            setEditorValue({
                html: initialContent.html || initialContent.plainText || '',
                plainText: initialContent.plainText || '',
            });
            setCompanyIds(initialContent.companyIds || []);
            setTags(initialContent.tags || []);
            setSource(initialContent.source || 'ai-generated');
            setAiContext(initialContent.aiContext || null);
            setVersion(1);
        } else {
            // Blank new note
            setNoteId(null);
            setTitle('');
            setEditorValue({ html: '', plainText: '' });
            setCompanyIds([]);
            setTags([]);
            setSource('manual');
            setAiContext(null);
            setVersion(1);
        }
    }, [isOpen, externalNoteId, initialContent, fetchNoteById]);

    // ── Manual save ─────────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        if (!title.trim()) {
            toast.warning('Please add a title.');
            return;
        }
        setIsSaving(true);
        try {
            const content = {
                type: 'doc',
                html: editorValue.html,
                plainText: editorValue.plainText,
            };

            if (noteId) {
                // Update existing
                const res = await updateNote(noteId, { title, content, companyIds, tags });
                setVersion(res.version || version + 1);
                toast.success('Note saved');
            } else {
                // Create new
                const res = await createNote({
                    title,
                    content,
                    companyIds,
                    tags,
                    source,
                    aiContext,
                });
                setNoteId(res.noteId);
                setVersion(1);
                toast.success('Note created');
            }
            fetchNotes(); // refresh list
        } catch (err) {
            console.error('[NotesPanel] save failed:', err);
            toast.error('Failed to save note.');
        } finally {
            setIsSaving(false);
        }
    }, [noteId, title, editorValue, companyIds, tags, source, aiContext, version, updateNote, createNote, fetchNotes]);

    // Ctrl+S handler from editor
    useEffect(() => {
        const handler = () => handleSave();
        const el = panelRef.current;
        el?.addEventListener('note-save', handler);
        return () => el?.removeEventListener('note-save', handler);
    }, [handleSave]);

    // ── Company ID quick-add ────────────────────────────────────────────
    const addCompany = () => {
        const id = companyInput.trim().toUpperCase();
        if (id && !companyIds.includes(id)) {
            setCompanyIds([...companyIds, id]);
        }
        setCompanyInput('');
    };

    // ── Version restore handler ─────────────────────────────────────────
    const handleVersionRestore = useCallback((data) => {
        setShowVersions(false);
        if (noteId) {
            fetchNoteById(noteId).then((d) => {
                if (!d) return;
                setTitle(d.title || '');
                const html = typeof d.content === 'string' ? d.content : d.content?.html || d.plainText || '';
                setEditorValue({ html, plainText: d.plainText || '' });
                setVersion(d.version || 1);
            });
        }
        toast.success(`Restored to version ${data.restoredFrom}`);
    }, [noteId, fetchNoteById]);

    if (!isOpen) return null;

    const saveLabel =
        saveStatus === 'saving' ? 'Saving...' :
            saveStatus === 'saved' ? `Saved ${lastSaved ? new Date(lastSaved).toLocaleTimeString() : ''}` :
                saveStatus === 'error' ? 'Save failed' :
                    noteId ? 'Auto-save on' : 'Not saved yet';

    return (
        <>
            <div className="notes-panel-backdrop" onClick={onClose} />
            <div className="notes-panel" ref={panelRef}>
                {/* Header */}
                <div className="notes-panel-header">
                    <div className="notes-panel-header-left">
                        <h2 className="notes-panel-heading">
                            {noteId ? 'Edit Note' : 'New Note'}
                        </h2>
                        <span className={`notes-save-indicator ${saveStatus}`}>
                            {saveLabel}
                        </span>
                    </div>
                    <div className="notes-panel-header-actions">
                        {noteId && (
                            <button
                                className="notes-panel-btn notes-panel-btn--ghost"
                                onClick={() => setShowVersions(true)}
                                title="Version history"
                            >
                                History
                            </button>
                        )}
                        <button className="notes-panel-close" onClick={onClose}>&times;</button>
                    </div>
                </div>

                {/* Title */}
                <div className="notes-panel-title-row">
                    <input
                        className="notes-title-input"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Note title..."
                        autoFocus
                    />
                </div>

                {/* Editor */}
                <div className="notes-panel-editor">
                    <NoteEditor
                        value={editorValue}
                        onChange={setEditorValue}
                        autoFocus={!title}
                    />
                </div>

                {/* Meta */}
                <div className="notes-panel-meta">
                    {/* Companies */}
                    <div className="notes-meta-section">
                        <label className="notes-meta-label">Companies</label>
                        <div className="notes-company-input-row">
                            <input
                                className="notes-company-input"
                                type="text"
                                value={companyInput}
                                onChange={(e) => setCompanyInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCompany(); } }}
                                placeholder="Symbol (e.g. HDFCBANK)"
                            />
                            <button className="notes-company-add" onClick={addCompany}>+</button>
                        </div>
                        {companyIds.length > 0 && (
                            <div className="notes-company-pills">
                                {companyIds.map((c) => (
                                    <span key={c} className="notes-company-pill">
                                        {c}
                                        <button
                                            onClick={() => setCompanyIds(companyIds.filter(x => x !== c))}
                                            className="notes-company-pill-remove"
                                        >&times;</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Tags */}
                    <div className="notes-meta-section">
                        <label className="notes-meta-label">Tags</label>
                        <TagInput tags={tags} onChange={setTags} />
                    </div>
                </div>

                {/* Footer */}
                <div className="notes-panel-footer">
                    <button
                        className="notes-save-btn"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : noteId ? 'Save Note' : 'Create Note'}
                    </button>
                    <span className="notes-panel-version">
                        {noteId ? `v${version}` : 'Draft'}
                    </span>
                </div>
            </div>

            {/* Version History overlay */}
            {showVersions && noteId && (
                <VersionHistory
                    noteId={noteId}
                    currentVersion={version}
                    onRestore={handleVersionRestore}
                    onClose={() => setShowVersions(false)}
                />
            )}
        </>
    );
}
